import React, { useState, useEffect, useCallback, useRef } from 'react';
import APIService from '../services/api';

/**
 * Livraison d'un dossier AO : où vont les dossiers candidats, qui valide, et la
 * copie vers le dossier final.
 *
 * Trois besoins distincts, volontairement séparés à l'écran :
 *
 *  1. L'emplacement de travail — le dossier kDrive où les dossiers candidats
 *     sont constitués pendant l'instruction du dossier.
 *  2. L'emplacement final — un autre dossier, choisi par l'administrateur, qui
 *     peut être fermé aux autres collaborateurs. Les pièces de la société n'ont
 *     pas à être visibles de toute l'équipe.
 *  3. La validation — un lot est validé par une personne, nommément. Le serveur
 *     enregistre le porteur du jeton : on ne valide jamais au nom d'un autre.
 *
 * Les lots affichés viennent du serveur, pas du rapport de scan : c'est lui qui
 * sait combien de lots existent et lesquels sont validés. Le rapport n'apporte
 * qu'un complément d'information (taux de documents, adéquation moyenne).
 *
 * Les refus 409 et 403 sont nominatifs (« déjà validé par Séverine ») : ils sont
 * affichés tels quels, jamais reformulés.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

const btn = {
  display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', borderRadius: '8px',
  border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)',
  fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
};
const btnMain = { ...btn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)', fontWeight: 700 };
const input = {
  width: '100%', padding: '9px 11px', borderRadius: '8px', border: '1px solid var(--input-border)',
  background: 'var(--input-bg, var(--surface))', color: 'var(--text)', fontFamily: 'inherit', fontSize: '13px',
};
const card = {
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px',
  padding: '18px 20px', marginBottom: '16px',
};
const h = { margin: '0 0 4px', fontSize: '14px', fontWeight: 700, color: 'var(--text-strong)' };
const sub = { margin: '0 0 14px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 };

/** « 12 juin » — la date suffit, l'heure n'apporte rien à une validation. */
const jour = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }); } catch (e) { return ''; }
};

const ACTIF = (st) => st === 'queued' || st === 'running';

export default function AoLivraison({ aoId, lotsScan = [], isAdmin, showNotification, onJob }) {
  const [data, setData] = useState(null);
  const [absent, setAbsent] = useState(false);   // routes pas encore servies
  const [chargement, setChargement] = useState(true);
  const [travail, setTravail] = useState('');
  const [final, setFinal] = useState('');
  const [enregistre, setEnregistre] = useState(false);
  const [lotOccupe, setLotOccupe] = useState(null);
  const [copie, setCopie] = useState(false);
  const [job, setJob] = useState(null);          // suivi de la copie en cours
  // Copie refusée faute de dossier AO sur kDrive : le serveur dit quoi faire,
  // on l'affiche et on propose les deux issues.
  const [blocage, setBlocage] = useState(null);  // { error, actions }
  const [reparation, setReparation] = useState(null);  // 'lier' | 'creer' | null
  const [chemin, setChemin] = useState('');
  const timer = useRef(null);

  const authHeaders = { Authorization: `Bearer ${APIService.getToken()}` };
  const jsonHeaders = { ...authHeaders, 'Content-Type': 'application/json' };

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/livraison`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
      if (r.status === 404 || r.status === 501) { setAbsent(true); setChargement(false); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { setAbsent(true); setChargement(false); return; }
      const d = j.data || j;
      setData(d);
      setTravail(d.config?.dossier_candidats || '');
      setFinal(d.config?.dossier_final || '');
      setAbsent(false);
    } catch (e) { setAbsent(true); }
    setChargement(false);
  }, [aoId]);

  useEffect(() => { charger(); }, [charger]);

  // Suivi de la copie : sondage tant que le traitement tourne, rien sinon.
  useEffect(() => {
    const id = job?.id;
    if (!id || !ACTIF(job?.statut)) return undefined;
    timer.current = setInterval(async () => {
      try {
        const r = await fetch(`${BASE}/jobs/${id}`, { headers: { Authorization: `Bearer ${APIService.getToken()}` } });
        if (!r.ok) return;
        const j = await r.json().catch(() => ({}));
        const nouveau = j.job || j.data || j;
        setJob({ id, ...nouveau });
        if (!ACTIF(nouveau.statut)) charger();
      } catch (e) { /* un sondage manqué se rattrape au suivant */ }
    }, 2000);
    return () => { clearInterval(timer.current); timer.current = null; };
  }, [job?.id, job?.statut, charger]);   // eslint-disable-line react-hooks/exhaustive-deps

  /** Message d'erreur du serveur, affiché tel quel : 409 et 403 nomment la personne. */
  const messageServeur = (j, defaut) => j?.message || j?.error || defaut;

  const enregistrerEmplacements = async () => {
    setEnregistre(true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/livraison/config`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify({ dossier_candidats: travail.trim() || null, dossier_final: final.trim() || null }),
      });
      if (r.status === 404 || r.status === 501) {
        setAbsent(true);
        showNotification?.('Les emplacements ne sont pas encore enregistrés côté serveur — rien n’a été écrit.', 'info');
      } else {
        const j = await r.json().catch(() => ({}));
        if (!r.ok || j.success === false) showNotification?.(messageServeur(j, 'Enregistrement refusé.'), 'error');
        else { showNotification?.('Emplacements enregistrés.', 'success'); charger(); }
      }
    } catch (e) { showNotification?.('Enregistrement impossible — réessayez.', 'error'); }
    setEnregistre(false);
  };

  /**
   * Validation d'un lot. Aucun corps de requête : le serveur enregistre le
   * porteur du jeton, et c'est bien ce qu'on veut — on valide en son nom propre.
   * Renvoie true si le serveur a enregistré, pour que la validation en série
   * sache ce qui est passé.
   */
  const validerLot = async (besoinId, retirer, { silencieux = false } = {}) => {
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/livraison/lots/${besoinId}/valider`, {
        method: retirer ? 'DELETE' : 'POST', headers: authHeaders,
      });
      if (r.status === 404 || r.status === 501) {
        setAbsent(true);
        if (!silencieux) showNotification?.('La validation n’est pas encore enregistrée côté serveur — rien n’a été écrit.', 'info');
        return false;
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) {
        // 409 (déjà validé par quelqu'un d'autre) et 403 (retrait interdit)
        // nomment la personne : le message du serveur est le bon.
        if (!silencieux) showNotification?.(messageServeur(j, 'Validation refusée.'), r.status === 409 ? 'info' : 'error');
        return false;
      }
      return true;
    } catch (e) {
      if (!silencieux) showNotification?.('Validation impossible — réessayez.', 'error');
      return false;
    }
  };

  const surLot = async (besoinId, retirer) => {
    setLotOccupe(String(besoinId));
    const ok = await validerLot(besoinId, retirer);
    if (ok) showNotification?.(retirer ? 'Validation retirée.' : 'Lot validé.', 'success');
    await charger();
    setLotOccupe(null);
  };

  // Pas de route « tout valider » : on enchaîne les lots non validés. Les refus
  // nominatifs sont remontés un par un, sans être noyés dans un compte global.
  const validerTout = async () => {
    const restants = (data?.lots || []).filter(l => !l.validation);
    if (!restants.length) return;
    setLotOccupe('tous');
    let faits = 0;
    const refus = [];
    for (const l of restants) {
      const r = await fetch(`${BASE}/ao/${aoId}/livraison/lots/${l.besoin_id}/valider`, { method: 'POST', headers: authHeaders })
        .catch(() => null);
      if (!r) { refus.push(`${l.titre || `Lot ${l.besoin_id}`} : injoignable`); continue; }
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.success !== false) faits += 1;
      else refus.push(`${l.titre || `Lot ${l.besoin_id}`} : ${messageServeur(j, `refus ${r.status}`)}`);
    }
    await charger();
    setLotOccupe(null);
    if (faits) showNotification?.(`${faits} lot${faits > 1 ? 's' : ''} validé${faits > 1 ? 's' : ''}.`, 'success');
    refus.forEach(m => showNotification?.(m, 'info'));
  };

  const copierVersFinal = async () => {
    setCopie(true);
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/livraison/copier`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({}) });
      if (r.status === 404 || r.status === 501) {
        setAbsent(true);
        showNotification?.('La copie vers le dossier final arrive côté serveur — rien n’a été copié.', 'info');
      } else {
        const j = await r.json().catch(() => ({}));
        // Dossier AO absent de kDrive : refus AVANT création du job, avec les
        // issues possibles. On ouvre la réparation plutôt qu'un simple message.
        if (r.status === 409 && j.code === 'kdrive_dossier_ao_introuvable') {
          setBlocage({ error: messageServeur(j, 'Le dossier de ce marché est introuvable sur kDrive.'), actions: j.actions || [] });
        }
        // 400 : aucun lot validé, par exemple. Le serveur explique, on affiche.
        else if (!r.ok || j.success === false) showNotification?.(messageServeur(j, 'Copie refusée.'), 'error');
        else if (j.job_id) {
          setJob({ id: j.job_id, statut: j.job?.statut || 'queued', ...(j.job || {}) });
          onJob?.({ job_id: j.job_id, job: j.job, count: j.count });
          showNotification?.('Copie lancée — vous serez prévenu à la fin.', 'success');
        } else { showNotification?.('Dossiers copiés vers le dossier final.', 'success'); charger(); }
      }
    } catch (e) { showNotification?.('Copie impossible — réessayez.', 'error'); }
    setCopie(false);
  };

  const reparer = async (quoi) => {
    setReparation(quoi);
    try {
      const r = quoi === 'lier'
        ? await fetch(`${BASE}/ao/${aoId}/link`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ root: chemin.trim() }) })
        : await fetch(`${BASE}/ao/${aoId}/apply`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({}) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { showNotification?.(messageServeur(j, 'Opération refusée.'), 'error'); return; }
      showNotification?.(quoi === 'lier' ? 'Dossier kDrive lié.' : 'Structure créée sur kDrive.', 'success');
      setBlocage(b => (b ? { ...b, repare: true } : b));
      await charger();
    } catch (e) { showNotification?.('Opération impossible — réessayez.', 'error'); }
    finally { setReparation(null); }
  };

  const lots = data?.lots || [];
  // Complément venu du scan, rapproché par besoin_id : le serveur ne connaît ni
  // le taux de documents ni l'adéquation.
  const scanDe = (besoinId) => lotsScan.find(l => String(l.besoinId) === String(besoinId)) || null;
  const prete = data?.prete === true;
  const valides = data?.lots_valides ?? lots.filter(l => l.validation).length;
  const total = data?.lots_total ?? lots.length;
  const resultat = job && !ACTIF(job.statut) ? (job.result || job.resultat || null) : null;

  if (chargement) {
    return (
      <div style={{ ...card, textAlign: 'center', color: 'var(--text-2)' }}>
        <BI icon="arrow-repeat" style={{ fontSize: '20px', animation: 'ao-spin .9s linear infinite' }} />
        <div style={{ marginTop: '8px', fontSize: '13px' }}>Lecture de la livraison…</div>
      </div>
    );
  }

  return (
    <div>
      {/* Réparation du dossier AO sur kDrive : le serveur refuse la copie avant
          de créer un traitement, et indique les issues. On les présente sans
          jargon — « lier » ou « créer », puis relancer. */}
      {blocage && (
        <div onClick={() => setBlocage(null)} style={{ position: 'fixed', inset: 0, zIndex: 620, background: 'rgba(5,6,8,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 'min(520px, 96vw)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px' }}>
            <div style={{ fontSize: '15.5px', fontWeight: 700, color: 'var(--text-strong)' }}>La copie n’a pas pu démarrer</div>
            <p style={{ margin: '7px 0 0', fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.55 }}>{blocage.error}</p>

            {blocage.repare ? (
              <div style={{ marginTop: '16px' }}>
                <p style={{ margin: '0 0 12px', fontSize: '12.5px', color: '#15803d', fontWeight: 600 }}>
                  <BI icon="check-circle-fill" /> C’est en place. Vous pouvez relancer la copie.
                </p>
                <button onClick={() => { setBlocage(null); copierVersFinal(); }} style={btnMain}>
                  <BI icon="folder-symlink" /> Relancer la copie
                </button>
              </div>
            ) : (<>
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--text-2)', marginBottom: '5px' }}>Chemin du dossier kDrive de ce marché</div>
                <input style={input} value={chemin} onChange={e => setChemin(e.target.value)}
                  placeholder="Ex. Appels d'offres/2026/Nom du marché" />
              </div>
              <div style={{ display: 'flex', gap: '9px', marginTop: '14px', flexWrap: 'wrap' }}>
                <button onClick={() => reparer('lier')} disabled={!chemin.trim() || reparation != null}
                  title={chemin.trim() ? 'Utiliser ce dossier existant' : 'Indiquez d’abord le chemin du dossier'}
                  style={{ ...btn, opacity: (!chemin.trim() || reparation != null) ? 0.5 : 1 }}>
                  <BI icon={reparation === 'lier' ? 'arrow-repeat' : 'link-45deg'} style={reparation === 'lier' ? { animation: 'ao-spin .7s linear infinite' } : undefined} />
                  Lier un dossier existant
                </button>
                <button onClick={() => reparer('creer')} disabled={reparation != null} style={{ ...btnMain, opacity: reparation != null ? 0.5 : 1 }}>
                  <BI icon={reparation === 'creer' ? 'arrow-repeat' : 'folder-plus'} style={reparation === 'creer' ? { animation: 'ao-spin .7s linear infinite' } : undefined} />
                  Créer la structure sur kDrive
                </button>
              </div>
            </>)}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={() => setBlocage(null)} style={btn}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {absent && (
        <div style={{ ...card, background: 'var(--warn-bg, #fffbeb)', borderColor: 'var(--warn-bd, #fde68a)', display: 'flex', gap: '11px', alignItems: 'flex-start' }}>
          <BI icon="clock-history" style={{ color: '#b45309', fontSize: '15px', marginTop: '1px' }} />
          <div style={{ fontSize: '12.5px', color: '#92400e', lineHeight: 1.55 }}>
            <strong>Cette partie arrive côté serveur.</strong> Vous pouvez préparer les emplacements et voir l’état des lots,
            mais rien n’est encore enregistré : ni les emplacements, ni les validations, ni la copie.
          </div>
        </div>
      )}

      {/* ── 1. Emplacements kDrive ── */}
      {/* Lecture ouverte à tous : « où finit ce dossier ? » est une question
          légitime pour qui l'instruit. Seule la modification est réservée. */}
      {!isAdmin && (
        <div style={card}>
          <h3 style={h}>Où vont les dossiers candidats</h3>
          <p style={sub}>Seul un administrateur peut modifier ces emplacements.</p>
          {[['Dossier de travail des candidats', data?.config?.dossier_candidats, data?.config?.dossier_candidats_url],
            ['Dossier final (après validation)', data?.config?.dossier_final, data?.config?.dossier_final_url]].map(([lbl, val, url]) => (
            <div key={lbl} style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '11.5px', fontWeight: 700, color: 'var(--text-2)' }}>{lbl}</div>
              <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '12px', color: val ? 'var(--text-strong)' : 'var(--text-faint)', wordBreak: 'break-word' }}>
                {val || 'non renseigné'}
                {url && <a href={url} target="_blank" rel="noreferrer" title="Ouvrir dans kDrive" style={{ marginLeft: '7px', color: 'var(--accent)' }}><BI icon="box-arrow-up-right" /></a>}
              </div>
            </div>
          ))}
        </div>
      )}
      {isAdmin && (
        <div style={card}>
          <h3 style={h}>Où vont les dossiers candidats</h3>
          <p style={sub}>
            Le dossier de travail est celui que l’équipe utilise pendant l’instruction. Le dossier final peut être différent,
            et réservé à un cercle restreint : les pièces de la société n’ont pas à être visibles de tous les collaborateurs.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px' }}>
            <label style={{ display: 'block' }}>
              <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: 'var(--text-2)', marginBottom: '5px' }}>Dossier de travail des candidats</span>
              <input style={input} value={travail} onChange={e => setTravail(e.target.value)}
                placeholder="Ex. Appels d'offres/2026/Dossier en cours" />
              {/^https?:\/\//i.test(travail.trim()) && (
                <span style={{ display: 'block', marginTop: '5px', fontSize: '11px', color: 'var(--text-3)', lineHeight: 1.5 }}>
                  Adresse kDrive détectée — elle sera vérifiée à l'enregistrement. Ce champ reste
                  informatif : la copie part toujours de la structure du dossier AO analysé.
                </span>
              )}
            </label>
            <label style={{ display: 'block' }}>
              <span style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: 'var(--text-2)', marginBottom: '5px' }}>Dossier final (après validation)</span>
              <input style={input} value={final} onChange={e => setFinal(e.target.value)}
                placeholder="Ex. Appels d'offres/2026/Dossiers validés" />
              {/^https?:\/\//i.test(final.trim()) && (
                <span style={{ display: 'block', marginTop: '5px', fontSize: '11px', color: 'var(--text-3)', lineHeight: 1.5 }}>
                  Adresse kDrive détectée — elle sera vérifiée à l'enregistrement, et la copie livrera
                  dans ce dossier précis, même s'il est renommé ou déplacé ensuite.
                </span>
              )}
            </label>
          </div>
          <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <button onClick={enregistrerEmplacements} disabled={enregistre} style={{ ...btnMain, opacity: enregistre ? 0.6 : 1 }}>
              <BI icon={enregistre ? 'arrow-repeat' : 'check-lg'} style={enregistre ? { animation: 'ao-spin .7s linear infinite' } : undefined} />
              {enregistre ? 'Enregistrement…' : 'Enregistrer les emplacements'}
            </button>
            {data?.config_maj_le && (
              <span style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>Dernière modification le {jour(data.config_maj_le)}</span>
            )}
          </div>
        </div>
      )}

      {/* ── 2. Validation lot par lot ── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <h3 style={h}>Validation des lots</h3>
          {total > 0 && (
            <span style={{ fontSize: '12px', fontWeight: 700, color: valides === total ? '#15803d' : 'var(--text-2)' }}>
              {valides}/{total} validé{valides > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <p style={sub}>
          Chaque collaborateur valide les lots dont il répond, en son nom. La validation engage sur deux points : les documents
          des candidats sont complets, et leur adéquation au poste a été relue.
        </p>

        {lots.length === 0 ? (
          <div style={{ fontSize: '13px', color: 'var(--text-2)' }}>Aucun lot dans ce dossier pour le moment.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {lots.map(lot => {
              const v = lot.validation;
              const sc = scanDe(lot.besoin_id);
              const occupe = lotOccupe === String(lot.besoin_id);
              return (
                <div key={lot.besoin_id}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '11px 13px', borderRadius: '10px', border: '1px solid var(--hair)', background: v ? 'var(--ok-bg, #f0fdf4)' : 'transparent' }}>
                  <BI icon={v ? 'patch-check-fill' : 'circle'} style={{ color: v ? '#16a34a' : 'var(--text-faint)', fontSize: '15px' }} />
                  <div style={{ flex: 1, minWidth: '180px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>{lot.titre || `Lot ${lot.besoin_id}`}</div>
                    <div style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>
                      {lot.client ? `${lot.client} · ` : ''}
                      {lot.candidats ?? sc?.cands.length ?? 0} profil{(lot.candidats ?? 0) > 1 ? 's' : ''}
                      {sc && ` · documents ${sc.pct}%`}
                      {sc?.avg != null && ` · adéquation ${sc.avg}%`}
                    </div>
                    {/* « Ne pas envoyer » : DIT AVANT LE CLIC. Valider un lot
                        de quatre et n'en livrer que deux sans l'avoir su, c'est
                        découvrir la marque une fois le dossier parti. */}
                    {(lot.profils_ecartes ?? 0) > 0 && (
                      <div style={{ marginTop: '3px', fontSize: '11.5px', color: 'var(--danger-fg)', fontWeight: 600 }}>
                        <BI icon="send-slash" /> {lot.profils_ecartes} profil{lot.profils_ecartes > 1 ? 's' : ''} marqué{lot.profils_ecartes > 1 ? 's' : ''} « ne pas envoyer » — {lot.profils_ecartes > 1 ? 'ils ne partiront pas' : 'il ne partira pas'} dans la livraison
                      </div>
                    )}
                    {v && (
                      <div style={{ marginTop: '3px', fontSize: '11.5px', color: '#15803d', fontWeight: 600 }}>
                        Validé par {v.par || 'un collaborateur'}{jour(v.le) ? ` le ${jour(v.le)}` : ''}
                      </div>
                    )}
                  </div>
                  {v ? (
                    <button onClick={() => surLot(lot.besoin_id, true)} disabled={occupe} style={{ ...btn, opacity: occupe ? 0.6 : 1 }}
                      title="Seul le validateur ou un administrateur peut retirer cette validation">
                      <BI icon="x-lg" /> Retirer
                    </button>
                  ) : (
                    <button onClick={() => surLot(lot.besoin_id, false)} disabled={occupe} style={{ ...btn, opacity: occupe ? 0.6 : 1 }}>
                      <BI icon={occupe ? 'arrow-repeat' : 'check-lg'} style={occupe ? { animation: 'ao-spin .7s linear infinite' } : undefined} /> Valider ce lot
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {lots.some(l => !l.validation) && (
          <div style={{ marginTop: '14px' }}>
            <button onClick={validerTout} disabled={lotOccupe === 'tous'} style={{ ...btn, opacity: lotOccupe === 'tous' ? 0.6 : 1 }}>
              <BI icon={lotOccupe === 'tous' ? 'arrow-repeat' : 'check2-all'} style={lotOccupe === 'tous' ? { animation: 'ao-spin .7s linear infinite' } : undefined} />
              {lotOccupe === 'tous' ? 'Validation…' : `Valider les ${lots.filter(l => !l.validation).length} lots restants`}
            </button>
          </div>
        )}
      </div>

      {/* ── 3. Copie vers le dossier final ── */}
      <div style={card}>
        <h3 style={h}>Dossier fini et validé</h3>
        <p style={sub}>
          Une fois tous les lots validés, les dossiers candidats sont copiés vers le dossier final. Les dossiers de travail
          restent en place : c’est une copie, pas un déplacement.
        </p>

        {job && ACTIF(job.statut) && (
          <div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <BI icon="arrow-repeat" style={{ animation: 'ao-spin .9s linear infinite' }} />
            Copie en cours — vous pouvez quitter cet écran, le traitement continue.
          </div>
        )}

        {resultat && (
          <div style={{ marginBottom: '14px', padding: '12px 13px', borderRadius: '10px', border: '1px solid var(--hair)', background: 'var(--ok-bg, #f0fdf4)' }}>
            <div style={{ fontSize: '12.5px', fontWeight: 700, color: '#15803d' }}>
              <BI icon="check-circle-fill" /> {resultat.fichiers_copies ?? 0} fichier{(resultat.fichiers_copies ?? 0) > 1 ? 's' : ''} copié{(resultat.fichiers_copies ?? 0) > 1 ? 's' : ''}
            </div>
            {resultat.url && (
              <a href={resultat.url} target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '7px', fontSize: '12.5px', fontWeight: 600, color: 'var(--accent)' }}>
                <BI icon="box-arrow-up-right" /> Ouvrir le dossier Livraison
              </a>
            )}
            {job?.error && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#b45309', lineHeight: 1.5 }}>{job.error}</div>
            )}
            {(resultat.fichiers_remplaces ?? 0) > 0 && (
              <div style={{ marginTop: '5px', fontSize: '12px', color: 'var(--text-2)' }}>
                dont {resultat.fichiers_remplaces} déjà livré{resultat.fichiers_remplaces > 1 ? 's' : ''}, remplacé{resultat.fichiers_remplaces > 1 ? 's' : ''} par la nouvelle version
              </div>
            )}
            {/* Ce qui n'est PAS parti chez le client, et pourquoi — le contrat
                signé notamment. Une exclusion muette ressemblerait à un oubli. */}
            {Array.isArray(resultat.fichiers_exclus) && resultat.fichiers_exclus.length > 0 && (
              <div style={{ marginTop: '8px', fontSize: '11.5px', color: 'var(--text-2)', lineHeight: 1.6 }}>
                <span style={{ fontWeight: 600 }}>Non copié{resultat.fichiers_exclus.length > 1 ? 's' : ''} volontairement :</span>
                <ul style={{ margin: '3px 0 0', paddingLeft: '18px' }}>
                  {resultat.fichiers_exclus.map((e, i) => (
                    <li key={i}>{typeof e === 'string' ? e : [e.fichier || e.path, e.motif].filter(Boolean).join(' — ')}</li>
                  ))}
                </ul>
              </div>
            )}
            {/* Les PROFILS que la barrière a écartés, avec la raison qui avait
                été écrite. Sans cette liste, le dossier part amputé et on ne
                l'apprend que chez le client. */}
            {Array.isArray(resultat.profils_ecartes) && resultat.profils_ecartes.length > 0 && (
              <div style={{ marginTop: '8px', fontSize: '11.5px', color: 'var(--danger-fg)', lineHeight: 1.6 }}>
                <span style={{ fontWeight: 600 }}>
                  {resultat.profils_ecartes.length} profil{resultat.profils_ecartes.length > 1 ? 's' : ''} écarté{resultat.profils_ecartes.length > 1 ? 's' : ''} de l'envoi :
                </span>
                <ul style={{ margin: '3px 0 0', paddingLeft: '18px' }}>
                  {resultat.profils_ecartes.map((p, i) => (
                    <li key={i}>{[p.candidat || `candidat #${p.candidate_id}`, p.motif].filter(Boolean).join(' — ')}</li>
                  ))}
                </ul>
              </div>
            )}
            {Array.isArray(resultat.erreurs) && resultat.erreurs.length > 0 && (
              <ul style={{ margin: '9px 0 0', paddingLeft: '18px', fontSize: '11.5px', color: '#b45309' }}>
                {resultat.erreurs.map((e, i) => (
                  <li key={i}>
                    {typeof e === 'string' ? e
                      : [[e.path, e.fichier].filter(Boolean).join('/'), e.error || e.message || e.erreur || 'Erreur'].filter(Boolean).join(' — ')}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {!prete && total > 0 && (
          <div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginBottom: '12px' }}>
            {total - valides} lot{total - valides > 1 ? 's' : ''} encore à valider
            {!final.trim() && (isAdmin ? ' — et le dossier final reste à renseigner ci-dessus.' : ' — un administrateur doit aussi renseigner le dossier final.')}
          </div>
        )}

        <div style={{ fontSize: '11.5px', color: 'var(--text-3)', margin: '0 0 8px', lineHeight: 1.6 }}>
          Copie les dossiers des lots validés vers <span style={{ fontFamily: "'IBM Plex Mono',monospace" }}>Livraison/&lt;lot&gt;/&lt;candidat&gt;</span>.
          Un fichier déjà livré portant le même nom est remplacé ; rien n'est supprimé du dossier final.
          Le contrat signé du candidat n'est jamais copié. Après la copie, le dossier final est un instantané :
          une correction côté travail demande de relancer.
        </div>
        <button onClick={copierVersFinal} disabled={copie || !prete || (job && ACTIF(job.statut))}
          title={prete ? 'Copier les dossiers candidats des lots validés vers le dossier final' : 'Tous les lots doivent être validés'}
          style={{ ...btnMain, opacity: (copie || !prete || (job && ACTIF(job.statut))) ? 0.5 : 1, cursor: prete ? 'pointer' : 'not-allowed' }}>
          <BI icon={copie ? 'arrow-repeat' : 'folder-symlink'} style={copie ? { animation: 'ao-spin .7s linear infinite' } : undefined} />
          {copie ? 'Lancement…' : 'Copier vers le dossier final'}
        </button>
      </div>
    </div>
  );
}
